import { FitLine, FitZone } from './fitText';

/**
 * The two-tone pad — the modern press block: light grey stock, angled bars of
 * the shop's colour and black across the head and the foot, the name set in
 * both inks, a slanted plate in the corner, and a grid whose head is filled
 * with the colour.
 *
 * Two inks: the colour the shop picks, and black. Measured off the printed
 * block in A4 pixels and multiplied by `u` (the paper's width against A4), so
 * an A6 book is the same pad printed small.
 */

const INK = '#121212';
const PAPER = '#EAEAEA';
const FIELD = '#FFFFFF';

/** The head block's height, as a share of the paper's width. */
const HEAD_RATIO = 0.315;

/** The words this pad prints that the other styles word differently. */
const SPLIT_LABELS = {
    // No space before the visarga: on its own it prints with a dotted circle.
    bn: { memoNo: 'মেমো নং', inWords: 'কথায়', phone: 'ফোন', email: 'ইমেইল', colon: 'ঃ' },
    en: { memoNo: 'Memo No', inWords: 'In Words', phone: 'Phone', email: 'Email', colon: ' :' },
};

/** The angled bars across the head, and the plate the title sits on. */
function HeadArt({ accent, plateWidth }) {
    return (
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 800 160"
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, display: 'block' }}
        >
            {/* The two bars that run off the left edge. */}
            <polygon points="0,0 300,0 272,30 0,30" fill={INK} />
            <polygon points="296,0 306,0 278,30 268,30" fill={PAPER} />
            <polygon points="302,0 430,0 402,30 274,30" fill={accent} />
            {/* The plate in the corner, slanted off the right edge. */}
            <polygon points={`${800 - plateWidth},18 800,8 800,112 ${800 - plateWidth + 16},104`} fill={INK} />
            <polygon points={`${800 - plateWidth},18 ${800 - plateWidth + 54},15 ${800 - plateWidth + 44},104 ${800 - plateWidth + 16},104`} fill="#2C2C2C" />
        </svg>
    );
}

/** The bars that close the sheet at its foot. */
function FootArt({ accent }) {
    return (
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 800 60"
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, display: 'block' }}
        >
            <polygon points="0,18 300,18 272,60 0,60" fill={accent} />
            <polygon points="286,18 800,18 800,60 258,60" fill={INK} />
            <polygon points="300,18 420,18 392,60 272,60" fill="#8A8A8A" opacity="0.55" />
            {/* The press block's own dotted square. */}
            {[0, 1, 2].map((row) => [0, 1, 2, 3, 4, 5].map((col) => (
                <circle key={`${row}-${col}`} cx={38 + col * 13} cy={30 + row * 11} r="2.6" fill="#FFFFFF" />
            )))}
        </svg>
    );
}

/** "Name" in a solid tag, and the ruled box written beside it. */
function TagBox({ label, u, accent }) {
    const height = 32 * u;
    return (
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 * u, height }}>
            <div
                style={{
                    flex: 'none', minWidth: 92 * u, background: INK, color: '#FFFFFF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: `0 ${10 * u}px`, fontSize: 18 * u, fontWeight: 700, lineHeight: 1.2,
                    whiteSpace: 'nowrap', boxSizing: 'border-box',
                }}
            >
                <span>{label}</span>
            </div>
            <div style={{ flex: 1, background: FIELD, border: `${1.4 * u}px solid ${accent}` }} />
        </div>
    );
}

/** The ruled writing area: a filled head over white cells on grey stock. */
function SplitGrid({ memo, u, accent }) {
    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.2 * u}px solid ${INK}`;
    const last = memo.columns.length - 1;

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    {memo.columns.map((col, i) => (
                        <th
                            key={col.id}
                            style={{
                                background: accent,
                                color: '#FFFFFF',
                                height: 34 * u,
                                padding: `0 ${3 * u}px`,
                                fontSize: 18 * u,
                                fontWeight: 700,
                                lineHeight: 1.3,
                                textAlign: 'center',
                                border: rule,
                                borderLeft: (memo.columnLines || i === 0) ? rule : 'none',
                                borderRight: (memo.columnLines || i === last) ? rule : 'none',
                            }}
                        >
                            <span>{col.label || ' '}</span>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: memo.rows }, (_, i) => (
                    <tr key={i}>
                        {memo.columns.map((col, c) => (
                            <td
                                key={col.id}
                                style={{
                                    height: 30 * u,
                                    padding: 0,
                                    background: FIELD,
                                    borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                    borderRight: (memo.columnLines || c === last) ? rule : 'none',
                                    borderBottom: memo.rowLines && i < memo.rows - 1 ? rule : 'none',
                                }}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/** Total = / Advance = / Due =, filled into the corner of the same grid. */
function SplitTotals({ memo, labels, u }) {
    const rows = [
        memo.totals.total ? labels.total : null,
        memo.totals.advance ? labels.advance : null,
        memo.totals.due ? labels.due : null,
    ].filter(Boolean);

    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.2 * u}px solid ${INK}`;
    const span = Math.max(0, memo.columns.length - 2);

    if (!rows.length) {
        return <div style={{ borderTop: rule, borderLeft: rule, borderRight: rule, height: 0 }} />;
    }

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <tbody>
                {rows.map((label, i) => {
                    const lastRow = i === rows.length - 1;
                    return (
                        <tr key={label}>
                            {memo.columns.slice(0, span).map((col, c) => (
                                <td
                                    key={col.id}
                                    style={{
                                        background: FIELD,
                                        borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                        borderRight: memo.columnLines && c === span - 1 ? rule : 'none',
                                        borderBottom: lastRow ? rule : 'none',
                                        height: 30 * u,
                                        padding: 0,
                                    }}
                                />
                            ))}
                            <td
                                style={{
                                    border: rule,
                                    borderBottom: lastRow ? rule : 'none',
                                    background: INK,
                                    color: '#FFFFFF',
                                    height: 30 * u,
                                    padding: `0 ${8 * u}px`,
                                    fontSize: 18 * u,
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    textAlign: 'right',
                                }}
                            >
                                <span>{`${label} =`}</span>
                            </td>
                            <td
                                style={{
                                    border: rule,
                                    borderBottom: lastRow ? rule : 'none',
                                    background: FIELD,
                                    height: 30 * u,
                                    padding: 0,
                                }}
                            />
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

/* ------------------------------------------------------------------ *
 *  The sheet
 * ------------------------------------------------------------------ */

export default function SplitMemo({ memo, labels, title, accent, paperWidth, u }) {
    const { shop, show } = memo;
    const lang = memo.docLang === 'en' ? 'en' : 'bn';
    const words = SPLIT_LABELS[lang];
    const on = (key) => show[key] !== false;
    const pad = 36 * u;
    const headHeight = Math.round(paperWidth * HEAD_RATIO);
    const footHeight = Math.round(paperWidth * 0.075);
    const plateWidth = 250;
    const phones = [shop.phone, shop.phone2].filter((n) => String(n || '').trim());
    const email = String(shop.email || '').trim();
    const addressLine = shop.address.split('\n').map((l) => l.trim()).filter(Boolean).join(', ');
    // The press sets the first word of the name in black and the rest in the
    // shop's own colour; a one-word name is set in the colour alone.
    const nameParts = (shop.name || ' ').trim().split(/\s+/);
    const nameHead = nameParts.length > 1 ? nameParts[0] : '';
    const nameTail = nameParts.length > 1 ? nameParts.slice(1).join(' ') : (shop.name || ' ');
    const rows = [
        on('addressBand') && addressLine ? [labels.address, addressLine] : null,
        on('phone') && phones.length ? [words.phone, phones.join(', ')] : null,
        on('email') && email ? [words.email, email] : null,
    ].filter(Boolean);
    const headKey = [
        memo.bismillah, title, shop.name, memo.proprietor, memo.distributor, shop.tagline,
        rows.map((r) => r.join('')).join('|'), shop.logo ? '1' : '0', JSON.stringify(show), u,
    ].join('~');

    return (
        <>
            {/* ---- the head: angled bars, the name, and the plate ---- */}
            <div style={{ position: 'relative', flex: 'none', minHeight: headHeight }}>
                {on('art') ? <HeadArt accent={accent} plateWidth={plateWidth} /> : null}

                {/* The title rides the slanted plate in the corner. */}
                {on('plate') ? (
                    <div
                        style={{
                            // Centred on the polygon drawn above: its middle sits
                            // at 84% across and 37% down the block.
                            position: 'absolute', zIndex: 1,
                            left: `${((800 - plateWidth / 2 - 10) / 800) * 100}%`,
                            top: headHeight * 0.375,
                            width: ((plateWidth - 86) / 800) * paperWidth,
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center', color: '#FFFFFF',
                        }}
                    >
                        <FitLine text={title} size={26 * u} style={{ fontWeight: 800, lineHeight: 1.2, textAlign: 'center' }} />
                    </div>
                ) : null}

                <FitZone
                    height={headHeight}
                    align="bottom"
                    pad={{ top: 48 * u, side: pad, bottom: 10 * u }}
                    signature={headKey}
                >
                    <div
                        style={{
                            display: 'flex', alignItems: 'flex-start', gap: 14 * u,
                            // Clear of the plate in the corner, so the shop's name
                            // is cut to the room it really has.
                            paddingRight: on('plate') ? ((plateWidth - 6) / 800) * paperWidth : 0,
                        }}
                    >
                        {on('logo') && shop.logo ? (
                            <div
                                aria-hidden="true"
                                style={{
                                    flex: 'none', width: 72 * u, height: 54 * u,
                                    backgroundImage: `url("${shop.logo}")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'left top',
                                    backgroundSize: 'contain',
                                }}
                            />
                        ) : null}

                        <div style={{ flex: 1, minWidth: 0 }}>
                            {on('bismillah') && memo.bismillah.trim() ? (
                                <div style={{ fontSize: 14 * u, fontWeight: 500, lineHeight: 1.3, color: '#4A4A4A' }}>
                                    {memo.bismillah}
                                </div>
                            ) : null}

                            {/* Two inks in one line: the first word black, the rest
                                in the shop's colour. */}
                            <FitLine
                                text={`${nameHead}${nameHead ? ' ' : ''}${nameTail}`}
                                size={46 * u}
                                style={{ fontWeight: 800, lineHeight: 1.12, color: accent }}
                                parts={nameHead ? [{ text: `${nameHead} `, color: INK }, { text: nameTail, color: accent }] : null}
                            />

                            {on('proprietor') && memo.proprietor.trim() ? (
                                <FitLine
                                    text={memo.proprietor}
                                    size={16 * u}
                                    style={{ marginTop: 1 * u, fontWeight: 700, lineHeight: 1.3, color: INK }}
                                />
                            ) : null}

                            {/* Address / phone / email, each on its own ruled row. */}
                            {rows.length ? (
                                <div style={{ marginTop: 3 * u }}>
                                    {rows.map(([label, value]) => (
                                        <div key={label} style={{ display: 'flex', gap: 6 * u, fontSize: 14 * u, lineHeight: 1.4, color: INK }}>
                                            <span style={{ flex: 'none', width: 72 * u, fontWeight: 600 }}>{`${label}${words.colon}`}</span>
                                            <span style={{ flex: 1, minWidth: 0, fontWeight: 500, overflow: 'hidden', whiteSpace: 'nowrap' }}>{value}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {on('distributor') && memo.distributor.trim() ? (
                                <FitLine
                                    text={memo.distributor}
                                    size={14 * u}
                                    style={{ marginTop: 2 * u, fontWeight: 600, lineHeight: 1.3, color: INK }}
                                />
                            ) : null}
                            {on('tagline') && shop.tagline.trim() ? (
                                <div style={{ marginTop: 1 * u, fontSize: 13 * u, fontWeight: 500, lineHeight: 1.35, color: '#4A4A4A' }}>
                                    {shop.tagline}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </FitZone>
            </div>

            {/* The rule that closes the head. */}
            <div style={{ flex: 'none', height: 2 * u, background: INK }} />

            {/* ---- the form ---- */}
            <div
                style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    padding: `${16 * u}px ${pad}px ${10 * u}px`, boxSizing: 'border-box', color: INK,
                }}
            >
                {(memo.fields.memoNo || memo.fields.date) ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 * u, marginBottom: 12 * u }}>
                        <span style={{ fontSize: 19 * u, fontWeight: 700, lineHeight: 1.4 }}>
                            {memo.fields.memoNo ? `${words.memoNo}${words.colon}` : ''}
                        </span>
                        {memo.fields.date ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 * u }}>
                                <span style={{ fontSize: 19 * u, fontWeight: 700, lineHeight: 1.4 }}>{labels.date}</span>
                                {/* One box cut into three by two strokes. */}
                                <span
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
                                        width: 190 * u, height: 30 * u, background: FIELD,
                                        border: `${1.4 * u}px solid ${INK}`, boxSizing: 'border-box',
                                        fontSize: 18 * u, fontWeight: 600, color: '#7A7A7A',
                                    }}
                                >
                                    <span>/</span>
                                    <span>/</span>
                                </span>
                            </span>
                        ) : null}
                    </div>
                ) : null}

                {memo.fields.name ? <TagBox label={labels.name} u={u} accent={accent} /> : null}
                {memo.fields.address ? (
                    <div style={{ marginTop: 7 * u }}>
                        <TagBox label={labels.address} u={u} accent={accent} />
                    </div>
                ) : null}

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: 10 * u }}>
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
                        <SplitGrid memo={memo} u={u} accent={accent} />
                    </div>
                    <SplitTotals memo={memo} labels={labels} u={u} />

                    {on('slogan') && memo.slogan.trim() ? (
                        <div
                            aria-hidden="true"
                            style={{
                                position: 'absolute', left: '10%', right: '32%', bottom: '12%',
                                textAlign: 'center', pointerEvents: 'none',
                                fontSize: 28 * u, fontWeight: 600, lineHeight: 1.2,
                                color: INK, opacity: 0.1, whiteSpace: 'nowrap',
                                transform: 'skewX(-10deg)',
                            }}
                        >
                            {memo.slogan}
                        </div>
                    ) : null}
                </div>

                {memo.showWords ? (
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 * u, height: 30 * u, marginTop: 12 * u }}>
                        <span
                            style={{
                                flex: 'none', background: accent, color: '#FFFFFF',
                                display: 'flex', alignItems: 'center', padding: `0 ${12 * u}px`,
                                fontSize: 18 * u, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap',
                            }}
                        >
                            <span>{`${words.inWords}${words.colon}`}</span>
                        </span>
                        <span style={{ flex: 1, background: '#E2E2E2', border: `${1.2 * u}px solid #C9C9C9` }} />
                    </div>
                ) : null}

                {memo.footerNote ? (
                    <div style={{ marginTop: 8 * u, fontSize: 15 * u, fontWeight: 500, lineHeight: 1.4, color: '#4A4A4A' }}>
                        {memo.footerNote}
                    </div>
                ) : null}

                <div style={{ flex: 'none', minHeight: 30 * u }} />

                {memo.showSignature ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 30 * u, marginTop: 4 * u }}>
                        {[labels.customerSign, labels.sellerSign].map((caption) => (
                            <div key={caption} style={{ width: 210 * u, textAlign: 'center' }}>
                                <div style={{ borderTop: `${1.6 * u}px dotted ${INK}`, paddingTop: 4 * u, fontSize: 16 * u, fontWeight: 600, lineHeight: 1.35 }}>
                                    {caption}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>

            {/* ---- the bars that close the sheet ---- */}
            {on('art') ? (
                <div style={{ position: 'relative', flex: 'none', height: footHeight }}>
                    <FootArt accent={accent} />
                </div>
            ) : null}
        </>
    );
}
